<template>
  <div>
    <Header />
    <v-container>

      <!-- Page header -->
      <v-row class="mb-4" align="center">
        <v-col cols="12" sm="auto">
          <h2 class="title">
            <v-icon class="mr-2">mdi-wardrobe-outline</v-icon>
            Stickerei Regalverwaltung
          </h2>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" sm="auto">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            label="Slot suchen..."
            hide-details
            dense
            outlined
            clearable
            style="min-width: 220px;"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn color="primary" @click="openSlotForm()">
            <v-icon left>mdi-plus</v-icon>
            Slot hinzufügen
          </v-btn>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn @click="fetchData" :loading="loading">
            <v-icon left>mdi-refresh</v-icon>
            Aktualisieren
          </v-btn>
        </v-col>
      </v-row>

      <!-- Loading -->
      <v-row v-if="loading">
        <v-col cols="12" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        </v-col>
      </v-row>

      <!-- Slot Cards -->
      <v-row v-else>
        <v-col
          v-for="slot in filteredSlots"
          :key="slot.id"
          cols="6" sm="4" md="3" lg="2"
        >
          <v-card
            class="slot-card"
            :class="stockClass(slot)"
            @click="openSlotDetail(slot)"
          >
            <!-- Stock fill bar along the left edge -->
            <div class="stock-edge" :style="{ height: stockPercent(slot) + '%' }"></div>

            <v-card-text class="slot-card-body pa-3">
              <div class="slot-code">{{ slot.code }}</div>
              <div class="slot-name mt-1">{{ slot.productName || slot.name || '–' }}</div>
              <div class="slot-chips mt-2">
                <span v-if="slot.productSku" class="chip-sku">{{ slot.productSku }}</span>
              </div>
              <div class="stock-row mt-2">
                <span class="stock-current">{{ slot.currentStock }}</span>
                <span class="stock-sep">/</span>
                <span class="stock-max">{{ slot.maxStock }}</span>
              </div>
              <v-progress-linear
                :value="stockPercent(slot)"
                :color="stockColor(slot)"
                height="5"
                rounded
                background-color="rgba(0,0,0,0.08)"
                class="mt-2"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="!loading && filteredSlots.length === 0" cols="12">
          <v-alert type="info">Keine Slots vorhanden.</v-alert>
        </v-col>
      </v-row>
    </v-container>

    <!-- ── Slot Detail Modal ── -->
    <v-dialog v-model="detail.show" max-width="540px" @keydown.esc="detail.show = false">
      <v-card v-if="detail.slot">
        <v-toolbar dense dark :color="stockColor(detail.slot)">
          <v-toolbar-title>
            <strong>{{ detail.slot.code }}</strong>
            <span class="ml-2" style="font-weight: 400; font-size: 14px;">{{ detail.slot.productName || detail.slot.name || '' }}</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="detail.show = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>

        <v-tabs v-model="detail.tab" grow>
          <v-tab><v-icon left small>mdi-package-variant</v-icon>Bestand</v-tab>
          <v-tab><v-icon left small>mdi-history</v-icon>Verlauf</v-tab>
          <v-tab><v-icon left small>mdi-pencil</v-icon>Bearbeiten</v-tab>
        </v-tabs>

        <v-tabs-items v-model="detail.tab">

          <!-- Tab 0: Stock overview + correction -->
          <v-tab-item>
            <v-card-text>
              <!-- Stock display -->
              <div class="text-center mb-4">
                <div style="font-size: 48px; font-weight: 900; line-height: 1;">{{ detail.slot.currentStock }}</div>
                <div class="grey--text">von {{ detail.slot.maxStock }} im Regal</div>
                <v-progress-linear
                  :value="stockPercent(detail.slot)"
                  :color="stockColor(detail.slot)"
                  height="10"
                  rounded
                  background-color="grey lighten-3"
                  class="mt-3"
                ></v-progress-linear>
              </div>

              <!-- Product info chips -->
              <div class="d-flex justify-center flex-wrap mb-4" style="gap: 6px;">
                <v-chip v-if="detail.slot.productSku" small color="deep-purple lighten-5" text-color="deep-purple darken-2">
                  {{ detail.slot.productSku }}
                </v-chip>
                <v-chip v-if="detail.slot.productEan" small color="blue lighten-5" text-color="blue darken-2">
                  {{ detail.slot.productEan }}
                </v-chip>
              </div>

              <!-- Stock correction -->
              <v-divider class="mb-3"></v-divider>
              <div class="subtitle-2 mb-2">Bestandskorrektur</div>

              <v-text-field
                v-model.number="book.qty"
                label="Neuer Bestand"
                type="number"
                min="0"
                outlined
                dense
                class="mb-2"
              ></v-text-field>
              <v-text-field
                v-model="book.note"
                label="Notiz (optional)"
                outlined
                dense
                class="mb-3"
              ></v-text-field>

              <div class="d-flex justify-end">
                <v-btn color="primary" @click="saveBooking" :loading="book.saving" :disabled="book.qty == null">
                  <v-icon left small>mdi-tune</v-icon>
                  Korrigieren
                </v-btn>
              </div>
            </v-card-text>
          </v-tab-item>

          <!-- Tab 1: Log -->
          <v-tab-item>
            <!-- Date filter -->
            <div class="pa-3 pb-0">
              <v-menu
                v-model="log.datePicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="log.dateFilter"
                    label="Datum filtern (optional)"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    dense
                    outlined
                    clearable
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="log.dateFilter = ''"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="log.dateFilter"
                  no-title
                  @input="log.datePicker = false"
                ></v-date-picker>
              </v-menu>
            </div>

            <v-progress-linear v-if="detail.logLoading" indeterminate class="mt-2"></v-progress-linear>

            <div class="log-scroll">
              <v-list dense v-if="filteredLog.length">
                <v-list-item v-for="entry in filteredLog" :key="entry.id">
                  <v-list-item-icon class="mr-2">
                    <v-icon small :color="logColor(entry.type)">{{ logIcon(entry.type) }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title style="font-size: 12px;">
                      <strong>{{ entry.qty > 0 ? '+' : '' }}{{ entry.qty }}</strong>
                      <span class="ml-2 grey--text">{{ entry.type }}</span>
                      <span v-if="entry.note" class="ml-2">– {{ entry.note }}</span>
                    </v-list-item-title>
                    <v-list-item-subtitle style="font-size: 10px;">
                      {{ formatDate(entry.createdAt) }}
                      <span v-if="entry.user" class="ml-1">· {{ entry.user }}</span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <div v-else-if="!detail.logLoading" class="pa-4 text-center grey--text">
                {{ log.dateFilter ? 'Keine Einträge für dieses Datum' : 'Keine Einträge' }}
              </div>
            </div>
          </v-tab-item>

          <!-- Tab 2: Edit slot -->
          <v-tab-item>
            <v-card-text>
              <v-form ref="slotForm" v-model="edit.valid">
                <v-text-field
                  v-model="edit.code"
                  label="Code * (z.B. A1)"
                  outlined dense
                  :rules="[v => !!v || 'Pflichtfeld']"
                  class="mb-2"
                ></v-text-field>
                <v-text-field
                  v-model="edit.name"
                  label="Name / Bezeichnung"
                  outlined dense
                  class="mb-2"
                ></v-text-field>
                <v-autocomplete
                  v-model="edit.productId"
                  :items="products"
                  item-text="name"
                  item-value="id"
                  label="Produkt zuweisen (optional)"
                  outlined dense
                  clearable
                  class="mb-2"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.sku }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
                <v-text-field
                  v-model.number="edit.maxStock"
                  label="Max. Bestand"
                  type="number"
                  min="0"
                  outlined dense
                ></v-text-field>
              </v-form>
              <div class="d-flex justify-space-between mt-2">
                <v-btn color="error" text @click="confirmDelete(detail.slot)">
                  <v-icon left small>mdi-delete</v-icon>
                  Slot löschen
                </v-btn>
                <div style="display: flex; gap: 8px;">
                  <v-btn text @click="printLabel(detail.slot)" :loading="detail.printing">
                    <v-icon left small>mdi-printer</v-icon>
                    Label drucken
                  </v-btn>
                  <v-btn color="primary" @click="saveSlot" :loading="edit.saving" :disabled="!edit.valid">
                    Speichern
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-tab-item>

        </v-tabs-items>
      </v-card>
    </v-dialog>

    <!-- ── New Slot Form ── -->
    <v-dialog v-model="newSlot.show" max-width="480px" @keydown.esc="newSlot.show = false">
      <v-card>
        <v-card-title>Neuer Slot</v-card-title>
        <v-card-text>
          <v-form ref="newSlotForm" v-model="newSlot.valid">
            <v-text-field
              v-model="newSlot.code"
              label="Code * (z.B. A1)"
              outlined dense
              :rules="[v => !!v || 'Pflichtfeld']"
              class="mb-2"
            ></v-text-field>
            <v-text-field
              v-model="newSlot.name"
              label="Name / Bezeichnung"
              outlined dense
              class="mb-2"
            ></v-text-field>
            <v-autocomplete
              v-model="newSlot.productId"
              :items="products"
              item-text="name"
              item-value="id"
              label="Produkt zuweisen (optional)"
              outlined dense
              clearable
              class="mb-2"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.sku }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
            <v-text-field
              v-model.number="newSlot.maxStock"
              label="Max. Bestand"
              type="number"
              min="0"
              outlined dense
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="newSlot.show = false" :disabled="newSlot.saving">Abbrechen</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="createSlot" :loading="newSlot.saving" :disabled="!newSlot.valid">
            Erstellen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Delete Confirm ── -->
    <v-dialog v-model="deleteDialog.show" max-width="360px">
      <v-card>
        <v-card-title>Slot löschen?</v-card-title>
        <v-card-text>
          Slot <strong>{{ deleteDialog.slot && deleteDialog.slot.code }}</strong> wirklich löschen?
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="deleteDialog.show = false">Abbrechen</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" @click="deleteSlot" :loading="deleteDialog.deleting">Löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top :timeout="3000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">OK</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Header from "@/views/private/header";
import productionService from "@/services/ProductionService";

export default {
  name: "EmbroideryShelf",
  components: { Header },
  data: () => ({
    loading: false,
    search: "",
    slots: [],
    products: [],

    detail: {
      show: false,
      slot: null,
      tab: 0,
      log: [],
      logLoading: false,
      printing: false,
    },

    book: {
      qty: null,
      note: "",
      saving: false,
    },

    log: {
      dateFilter: "",
      datePicker: false,
    },

    edit: {
      valid: false,
      saving: false,
      code: "",
      name: "",
      productId: null,
      maxStock: 0,
    },

    newSlot: {
      show: false,
      valid: false,
      saving: false,
      code: "",
      name: "",
      productId: null,
      maxStock: 0,
    },

    deleteDialog: {
      show: false,
      deleting: false,
      slot: null,
    },

    snackbar: { show: false, text: "", color: "success" },
  }),

  computed: {
    filteredSlots() {
      const base = !this.search
        ? this.slots
        : (() => {
            const q = this.search.toLowerCase();
            return this.slots.filter(s =>
              s.code.toLowerCase().includes(q) ||
              (s.productName && s.productName.toLowerCase().includes(q)) ||
              (s.productSku && s.productSku.toLowerCase().includes(q))
            );
          })();
      return [...base].sort((a, b) =>
        a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' })
      );
    },

    filteredLog() {
      if (!this.log.dateFilter) return this.detail.log;
      return this.detail.log.filter(entry => {
        if (!entry.createdAt) return false;
        return entry.createdAt.slice(0, 10) === this.log.dateFilter;
      });
    },
  },

  async mounted() {
    await this.fetchData();
  },

  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const [slotsRes, productsRes] = await Promise.all([
          productionService.getEmbroideryShelf(),
          productionService.getEmbroideryProducts(),
        ]);
        this.slots = slotsRes.data;
        this.products = productsRes.data;
      } catch {
        this.notify("Fehler beim Laden", "error");
      } finally {
        this.loading = false;
      }
    },

    // ── Slot detail ───────────────────────────────────────────────────────
    async openSlotDetail(slot) {
      this.detail.slot = slot;
      this.detail.tab = 0;
      this.detail.log = [];
      this.detail.show = true;

      // Pre-fill edit form
      this.edit.code = slot.code;
      this.edit.name = slot.name || "";
      this.edit.productId = slot.productId || null;
      this.edit.maxStock = slot.maxStock || 0;
      this.edit.saving = false;

      // Reset booking form
      this.book.qty = null;
      this.book.note = "";
      this.book.saving = false;

      // Reset log filter
      this.log.dateFilter = "";
      this.log.datePicker = false;

      // Load log in background
      this.detail.logLoading = true;
      try {
        const res = await productionService.getEmbroiderySlotLog(slot.id);
        this.detail.log = res.data;
      } catch { /* non-critical */ } finally {
        this.detail.logLoading = false;
      }
    },

    // ── Stock correction ──────────────────────────────────────────────────
    async saveBooking() {
      if (this.book.qty == null) return;
      this.book.saving = true;
      try {
        const updated = await productionService.bookEmbroideryStock(this.detail.slot.id, {
          type: 'CORRECTION',
          qty: this.book.qty,
          note: this.book.note || null,
        });
        // Update slot in list and in detail
        const idx = this.slots.findIndex(s => s.id === this.detail.slot.id);
        if (idx !== -1) this.$set(this.slots, idx, updated.data);
        this.detail.slot = updated.data;
        this.book.qty = null;
        this.book.note = "";
        this.notify("Buchung gespeichert");

        // Refresh log
        const logRes = await productionService.getEmbroiderySlotLog(this.detail.slot.id);
        this.detail.log = logRes.data;
      } catch (err) {
        this.notify(err.response?.data?.error || "Fehler beim Buchen", "error");
      } finally {
        this.book.saving = false;
      }
    },

    // ── Slot edit ─────────────────────────────────────────────────────────
    async saveSlot() {
      if (!this.$refs.slotForm.validate()) return;
      this.edit.saving = true;
      try {
        await productionService.updateEmbroiderySlot(this.detail.slot.id, {
          code: this.edit.code,
          name: this.edit.name || null,
          productId: this.edit.productId || null,
          maxStock: this.edit.maxStock || 0,
        });
        this.notify("Slot gespeichert");
        this.detail.show = false;
        await this.fetchData();
      } catch (err) {
        this.notify(err.response?.data?.error || "Fehler beim Speichern", "error");
      } finally {
        this.edit.saving = false;
      }
    },

    // ── New slot ──────────────────────────────────────────────────────────
    openSlotForm() {
      this.newSlot.code = "";
      this.newSlot.name = "";
      this.newSlot.productId = null;
      this.newSlot.maxStock = 0;
      this.newSlot.saving = false;
      this.newSlot.show = true;
      this.$nextTick(() => this.$refs.newSlotForm && this.$refs.newSlotForm.resetValidation());
    },

    async createSlot() {
      if (!this.$refs.newSlotForm.validate()) return;
      this.newSlot.saving = true;
      try {
        await productionService.createEmbroiderySlot({
          code: this.newSlot.code,
          name: this.newSlot.name || null,
          productId: this.newSlot.productId || null,
          maxStock: this.newSlot.maxStock || 0,
        });
        this.notify("Slot erstellt");
        this.newSlot.show = false;
        await this.fetchData();
      } catch (err) {
        this.notify(err.response?.data?.error || "Fehler beim Erstellen", "error");
      } finally {
        this.newSlot.saving = false;
      }
    },

    // ── Delete ────────────────────────────────────────────────────────────
    confirmDelete(slot) {
      this.deleteDialog.slot = slot;
      this.deleteDialog.deleting = false;
      this.deleteDialog.show = true;
    },

    async deleteSlot() {
      this.deleteDialog.deleting = true;
      try {
        await productionService.deleteEmbroiderySlot(this.deleteDialog.slot.id);
        this.deleteDialog.show = false;
        this.detail.show = false;
        this.notify("Slot gelöscht");
        await this.fetchData();
      } catch {
        this.notify("Fehler beim Löschen", "error");
      } finally {
        this.deleteDialog.deleting = false;
      }
    },

    // ── Print label ───────────────────────────────────────────────────────
    async printLabel(slot) {
      this.detail.printing = true;
      try {
        await productionService.printEmbroideryShelfLabel(slot.id);
        this.notify(`Label für ${slot.code} gedruckt`);
      } catch (err) {
        this.notify(err.response?.data?.error || "Fehler beim Drucken", "error");
      } finally {
        this.detail.printing = false;
      }
    },

    // ── Helpers ───────────────────────────────────────────────────────────
    stockPercent(slot) {
      if (!slot.maxStock) return 0;
      return Math.min(100, Math.round((slot.currentStock / slot.maxStock) * 100));
    },

    stockColor(slot) {
      if (!slot.maxStock) return "grey";
      const pct = slot.currentStock / slot.maxStock;
      if (pct <= 0) return "grey";
      if (pct < 0.25) return "error";
      if (pct < 0.6) return "warning";
      return "success";
    },

    stockClass(slot) {
      const color = this.stockColor(slot);
      return `stock-${color}`;
    },

    logColor(type) {
      return type === 'IN' ? 'success' : type === 'OUT' ? 'error' : 'warning';
    },
    logIcon(type) {
      return type === 'IN' ? 'mdi-arrow-down' : type === 'OUT' ? 'mdi-arrow-up' : 'mdi-tune';
    },

    formatDate(dt) {
      if (!dt) return '';
      return new Date(dt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },

    notify(text, color = "success") {
      this.snackbar = { show: true, text, color };
    },
  },
};
</script>

<style scoped>
.slot-card {
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.15s;
  border-left: 5px solid #ccc;
}
.slot-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(0,0,0,0.12);
}

.slot-card.stock-success { border-left-color: #43a047; }
.slot-card.stock-warning { border-left-color: #fb8c00; }
.slot-card.stock-error   { border-left-color: #e53935; }
.slot-card.stock-grey    { border-left-color: #9e9e9e; }

.slot-card-body {
  position: relative;
}

.slot-code {
  font-size: 22px;
  font-weight: 900;
  color: #212121;
  letter-spacing: 0.5px;
}
.slot-name {
  font-size: 12px;
  color: #666;
  line-height: 1.3;
  min-height: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.slot-chips {
  min-height: 18px;
}
.chip-sku {
  background: #ede7f6;
  color: #4527a0;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 8px;
}
.stock-row {
  display: flex;
  align-items: baseline;
  gap: 2px;
}
.stock-current { font-size: 20px; font-weight: 700; color: #212121; }
.stock-sep     { font-size: 14px; color: #bbb; }
.stock-max     { font-size: 14px; color: #888; }

/* Stock edge fill bar (decorative, on the left behind border) */
.stock-edge {
  display: none; /* visual accent handled via border-left */
}

.log-scroll {
  max-height: 360px;
  overflow-y: auto;
}
</style>
